<template>
  <div class="moment-tracker-item">
    <div class="users">
      <div class="users-left">
        <div class="pct">{{ pct }}%</div>
        <div class="count">
          <i class="material-icons">&#xE7FD;</i>
          {{ itemUsers.length }}
        </div>
      </div>
      <div class="users-right">
        <div class="key" :class="`key-${key}`">{{ key }}</div>
      </div>
    </div>
    <div class="type">{{ type }}</div>
    <simple-scatterplot
      ref="chart"
      :data="itemUsers"
      :config="config"
      @node-hover="onNodeHover"
      @node-click="onNodeClick"
    />
  </div>
</template>

<script>
import crossfilter2 from 'crossfilter2';
import { capitalize, differenceBy, camelCase } from 'lodash';
import { mapState, mapGetters } from 'vuex';
import { humanize } from 'utils/string';
import SimpleScatterplot from 'charts/SimpleScatterplot';
import { getUserById } from 'store/queries/audience';
import { usersCf } from 'store/filters/audience';
import {
  stationaryTypes,
  transportTypes,
  getEventKeypath,
} from 'config/settings';
import bus from 'store/busses/AudienceBus';

export default {
  name: 'MomentTrackerItem',
  props: {
    config: {
      type: Object,
      required: true,
    },
  },

  components: {
    SimpleScatterplot,
  },

  data() {
    return {
      cf: crossfilter2([]),
      dim: null,
      bus,
    };
  },

  watch: {
    config: function(newValue) {
      this.setDim(newValue);
    },

    'bus.userId'(userId) {
      this.$refs.chart.highlightNode(userId);
    },
  },

  created() {
    this.setDim(this.config);
  },

  computed: {
    ...mapState('audience', ['users']),

    ...mapGetters('account', ['momentDefinitions']),

    itemUsers() {
      this.dim.filterAll();
      this.cf.remove();
      this.cf.add(this.users);
      this.dim.filter(this.config.value);
      return this.cf.allFiltered();
    },

    pct() {
      if (this.users.length === 0) {
        return 0;
      }

      const pct = Math.round((this.itemUsers.length / this.users.length) * 100);

      if (pct === 0 && this.itemUsers.length > 0) {
        return 1;
      }

      return pct;
    },

    type() {
      const { key, value } = this.config;

      return this.getType(value, key);
    },

    key() {
      const { key } = this.config;

      return key.charAt(0);
    },
  },

  methods: {
    setDim(config) {
      const { key, category } = config;

      if (this.dim) {
        this.dim.filterAll();
      }

      this.dim = this.cf.dimension(
        d => d[`_${category}${capitalize(key)}`],
        true
      );
    },

    getType(value, key) {
      if (key === 'moments' && this.momentDefinitions) {
        const definition = this.momentDefinitions[value]; // id

        if (definition) {
          return definition.display_name;
        }
      }

      const keypath = getEventKeypath(value);

      return this.$t(keypath);
    },

    onNodeHover(data, pos) {
      this.$emit('node-hover', data, pos);
    },

    onNodeClick(data, pos) {
      this.$emit('node-click', data, pos);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.moment-tracker-item {
  display: flex;
  flex-direction: column;
}

.users {
  display: flex;
  justify-content: space-between;
}

.users-left {
  display: flex;
  align-items: baseline;
}

.users-right {
  display: flex;
}

.pct {
  font-size: 1.25rem;
  line-height: 0.8;
  color: rgba($black, 0.87);

  + .count {
    margin-left: 0.375rem;
  }
}

.count {
  font-size: $size-small;
  line-height: 1.3;
  color: rgba($black, 0.5);

  i {
    position: relative;
    top: 2px;
    font-size: $size-small;
  }
}

.type {
  font-size: 0.875rem;
  line-height: 1.29;
  color: rgba($black, 0.64);
  text-transform: lowercase;
}

.key {
  width: 1.125rem;
  height: 1.125rem;
  border-radius: 4px;
  font-size: $size-small;
  font-weight: $weight-bold;
  text-align: center;

  &.key-e {
    background-color: $white;
    color: rgba($black, 0.64);
    border: 1px solid rgba($black, 0.64);
    line-height: 1.2;
  }

  &.key-m {
    color: $white;
    background-color: rgba($black, 0.72);
    line-height: 1.4;
  }
}

.simple-scatterplot {
  display: flex;
  flex: 1;
}
</style>
